import { useMemo, ChangeEvent } from 'react';
import { AccordionItem, AccordionPanel, Flex } from '@chakra-ui/react';
import { FaCopy, FaTrash } from 'react-icons/fa';
import { AccordionButton, IconButton } from 'components/UI';
import ParameterEditSection from '../ParameterEditSection';
import ParameterValues from '../ParameterValues';
import { ParameterValueProps } from '../ParameterValues/ParameterValue';
import { IParameterList, IParameterModels, IReferenceParameterModel, INoteModel } from '../../../../server/types';

export type ParameterEditProps = {
  parameter: Record<string, any>;
  paramKey: string | number;
  parameterModels?: IParameterModels;
  updateSaveParameters(key: string | number, name: string | number, value: string | boolean): void;
  deleteListItem(paramKey: string | number, key: string | number, exKey: string | number): void;
  deleteExpression(paramKey: string | number, key: string | number, exKey: string | number): void;
  deleteParameter(deleteKey: string | number | undefined): void;
  addParameter(key: string | number, type: string, copyCurrentModel?: {}): void;
  parameterList: IParameterList[] | (string | boolean | string[])[][];
};

const ParameterEdit = ({
  parameter,
  paramKey,
  parameterModels,
  updateSaveParameters,
  deleteListItem,
  deleteExpression,
  addParameter,
  deleteParameter,
  parameterList,
}: ParameterEditProps) => {
  if (!parameter) return null;

  const handleSaveParameters: ParameterValueProps['updateSaveParameters'] = (
    name: string | number,
    value: string | boolean
  ) => {
    updateSaveParameters(paramKey, name, value);
  };

  const handleChangeText: ParameterValueProps['onChangeText'] = (e: { target: { name: string; value: string } }) => {
    handleSaveParameters(e.target.name, e.target.value);
  };

  const handleCheckboxClicked: ParameterValueProps['onCheckboxClicked'] = (e: ChangeEvent<HTMLInputElement>) => {
    handleSaveParameters(e.target.name, e.target.checked);
  };

  const handleDropDownChange: ParameterValueProps['onDropDownChange'] = (name: string, value: string) => {
    handleSaveParameters(name, value);
  };

  const handleFormatExpression = (data: Record<string, string>, expression: Record<string, string>) => {
    const formattedExpression = Object.entries(data).reduce(
      (acc, [expKey, expValue]) => ({
        ...acc,
        [expKey]: expValue,
      }),
      expression
    );

    return formattedExpression;
  };

  const handleExpressionChange: ParameterValueProps['onExpressionChange'] = (key, exKey, data, addNew) => {
    const conditions = parameter[key]?.expressions ?? {};
    const expression = conditions[exKey] ?? {};

    const newExp = handleFormatExpression(data, expression);

    const newCond = { ...conditions, [exKey]: newExp };

    const expressionList = parameter[key]?.expressionList?.length ? [...parameter[key].expressionList] : [];

    expressionList.push(exKey);

    const expressionListObj = addNew
      ? {
          expressionList,
        }
      : {};

    const updateParamObj = {
      ...parameter[key],
      expressions: newCond,
      ...expressionListObj,
    };

    updateSaveParameters(paramKey, key, updateParamObj);
  };

  const handleListItemChange: ParameterValueProps['onListItemChange'] = (key, exKey, data) => {
    let conditions = parameter[key] ?? {};
    if (parameter.type === 'boolean') {
      conditions = Array.isArray(conditions)
        ? conditions.map((item, idx) => ({
            displayName: Number(exKey) === idx ? data.displayName : item.displayName,
            value: Number(exKey) === idx ? data.value : item.value,
          }))
        : [
            { displayName: '', value: true },
            { displayName: '', value: false },
          ];

      updateSaveParameters(paramKey, key, conditions);
    } else {
      const newExp = handleFormatExpression(data, {});
      const newCond = { ...conditions, [exKey]: newExp };
      updateSaveParameters(paramKey, key, newCond);
    }
  };

  const handleAddCondition = (key: string | number) => {
    const condKey = Math.random().toString(36).substr(2, 9);
    const data = {
      if: 'If',
      expression: '',
      operator: 'AND',
    };
    handleExpressionChange(key, condKey, data, true);
  };

  const handleAddListItem: ParameterValueProps['addListItem'] = (key) => {
    const condKey = Math.random().toString(36).substr(2, 9);
    const updateParamObj = {
      ...parameter[key],
      [condKey]: {
        displayName: '',
        value: '',
      },
    };

    handleSaveParameters(key, updateParamObj);
  };

  const handleGetModel = () => {
    switch (parameter.parameterType) {
      case 'reference':
        return parameterModels?.referenceParameterModel;
      case 'note':
        return parameterModels?.noteModel;
      default:
        return parameterModels?.drivingParameterModel;
    }
  };

  const pListArray = (parameterList as string[][]).flatMap(([, values]) => values);

  const max = pListArray.reduce((a: number, b: string) => Math.max(+a, +b), 0) + 1;

  const condKey = max.toString();

  const handleCopyParameter = (
    currentParam: { [x: string]: string },
    newModel: IReferenceParameterModel | INoteModel | undefined
  ) => {
    const updatedParam: { [x: string]: string } = {
      ...currentParam,
      displayName: `Copy of ${currentParam.displayName}`,
    };

    return Object.entries(newModel as IReferenceParameterModel).reduce((acc, [key, value]) => {
      if (updatedParam[key] === undefined) return acc;

      return {
        ...acc,
        [key]: {
          ...value,
          default: updatedParam[key],
        },
      };
    }, {});
  };

  const model = handleGetModel();

  const renderParameterValues = useMemo(
    () => (
      <ParameterValues
        model={model}
        parameter={parameter}
        addListItem={handleAddListItem}
        deleteListItem={deleteListItem}
        onListItemChange={handleListItemChange}
        paramKey={paramKey}
        onChangeText={handleChangeText}
        updateSaveParameters={handleSaveParameters}
        onCheckboxClicked={handleCheckboxClicked}
        onDropDownChange={handleDropDownChange}
        onExpressionChange={handleExpressionChange}
        deleteExpression={deleteExpression}
        addCondition={handleAddCondition}
      />
    ),
    [model, parameter, paramKey]
  );

  return (
    <AccordionItem>
      <AccordionButton
        sx={{
          bgColor: 'base2Colour.500',
          _hover: { bgColor: 'base2Colour.500' },
          color: 'black',
        }}
      >
        {parameter.displayName}
      </AccordionButton>
      <AccordionPanel p={0}>
        <ParameterEditSection>
          {renderParameterValues}
          <Flex justifyContent="flex-start" gap={2} alignItems="center">
            <IconButton
              onClick={() => addParameter(condKey, 'copy', handleCopyParameter(parameter, model))}
              icon={<FaCopy />}
              title="Copy Parameter"
              aria-label="Copy Parameter"
            />
            <IconButton
              onClick={() => deleteParameter(paramKey)}
              title="Delete Parameter"
              aria-label="Delete Parameter"
              icon={<FaTrash />}
            />
          </Flex>
        </ParameterEditSection>
      </AccordionPanel>
    </AccordionItem>
  );
};

export default ParameterEdit;
